import { useCallback, useMemo } from 'react';
import useUserFeaturesStore from '../store/userFeaturesStore';
import { FEATURE_CODES, PUBLIC_UI_ELEMENTS, getFeatureByElement } from '../constants/featureElements';

/**
 * Hook kiểm tra quyền truy cập feature
 * KHÔNG gọi API - chỉ đọc state từ store
 * API được gọi bởi FeatureProvider khi user đăng nhập
 * @returns {Object} - Các hàm và state liên quan đến feature access
 */
const useFeatureAccess = () => {
  const {
    subscription,
    features,
    uiElements,
    loading,
    error,
    initialized,
    fetchUserFeatures,
    hasFeature,
    getFeature,
  } = useUserFeaturesStore();

  /**
   * Kiểm tra user có quyền xem element không
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  const canAccessElement = useCallback((elementName) => {
    // Public elements luôn hiển thị
    if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
      return true;
    }

    // Nếu đang loading và chưa có data -> chờ load xong mới quyết định
    if (loading) {
      return true;
    }

    // Nếu đã load xong (initialized=true) mà không có uiElements -> user không có quyền
    if (initialized && uiElements.length === 0) {
      return false;
    }

    // Nếu chưa có data và không loading -> chưa xác định được, tạm thời cho phép
    if (uiElements.length === 0) {
      return true;
    }

    // Kiểm tra element có trong danh sách được phép không
    return uiElements.includes(elementName);
  }, [uiElements, loading, initialized]);

  /**
   * Kiểm tra user có quyền truy cập feature không
   * @param {string} featureCode - Code của feature
   * @returns {boolean}
   */
  const canAccessFeature = useCallback((featureCode) => {
    // Dashboard và Plans luôn accessible
    if (featureCode === FEATURE_CODES.DASHBOARD || featureCode === FEATURE_CODES.PLANS) {
      return true;
    }

    // Nếu đang loading và chưa có data -> chờ load xong mới quyết định
    if (loading) {
      return true;
    }

    // Nếu đã load xong (initialized=true) mà không có features -> user không có quyền
    if (initialized && features.length === 0) {
      return false;
    }

    // Nếu chưa có data và không loading -> chưa xác định được, tạm thời cho phép
    if (features.length === 0) {
      return true;
    }

    return hasFeature(featureCode);
  }, [features, loading, initialized, hasFeature]);

  /**
   * Lấy giới hạn usage của feature
   * @param {string} featureCode - Code của feature
   * @returns {Object|null} - { limit_usage, limit_period }
   */
  const getFeatureLimit = useCallback((featureCode) => {
    const feature = getFeature(featureCode);
    if (!feature) return null;

    return {
      limitUsage: feature.limit_usage,
      limitPeriod: feature.limit_period,
      isUnlimited: feature.limit_usage === null,
    };
  }, [getFeature]);

  /**
   * Lấy feature code từ element name
   * @param {string} elementName - Tên component/element
   * @returns {string|null}
   */
  const getElementFeature = useCallback((elementName) => {
    return getFeatureByElement(elementName);
  }, []);

  // Memo các giá trị để tránh re-render không cần thiết
  const isLoaded = useMemo(() => initialized && !loading, [initialized, loading]);
  const hasSubscription = useMemo(() => subscription !== null, [subscription]);

  /**
   * Kiểm tra element có thể sử dụng (quyền + usage quota)
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  const canUseElement = useCallback((elementName) => {
    // Public elements luôn OK
    if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
      return true;
    }

    // Nếu đang loading hoặc chưa có data, cho phép tạm thời
    if (loading || features.length === 0) {
      return true;
    }

    // Tìm feature của element
    const featureCode = getFeatureByElement(elementName);
    if (!featureCode) return true;

    // Kiểm tra có thể dùng feature không (quyền + usage)
    const feature = getFeature(featureCode);
    if (!feature) return true;

    // Check permission first
    if (!uiElements.includes(elementName)) {
      return false;
    }

    // If no limit, allow
    if (feature.limit_usage === null || feature.limit_usage === undefined || feature.limit_usage < 0) {
      return true;
    }

    // Check usage
    return (feature.usage_count || 0) < feature.limit_usage;
  }, [loading, features, uiElements, getFeature]);

  /**
   * Kiểm tra nếu element hết usage (có quyền nhưng hết lượt)
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  const isUsageExhausted = useCallback((elementName) => {
    // Public elements không bao giờ hết
    if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
      return false;
    }

    if (loading || features.length === 0) {
      return false;
    }

    const featureCode = getFeatureByElement(elementName);
    if (!featureCode) return false;

    const feature = getFeature(featureCode);
    if (!feature) return false;

    // Có quyền truy cập không?
    const hasAccess = uiElements.includes(elementName);
    if (!hasAccess) return false;

    // Nếu không có limit, không bao giờ hết
    if (feature.limit_usage === null || feature.limit_usage === undefined || feature.limit_usage < 0) {
      return false;
    }

    // Hết usage khi used >= limit
    return (feature.usage_count || 0) >= feature.limit_usage;
  }, [loading, features, uiElements, getFeature]);

  /**
   * Lấy thông tin usage của element
   * @param {string} elementName - Tên component/element
   * @returns {Object|null}
   */
  const getElementUsageInfo = useCallback((elementName) => {
    const featureCode = getFeatureByElement(elementName);
    if (!featureCode) return null;

    const feature = getFeature(featureCode);
    if (!feature) return null;

    const usageCount = feature.usage_count || 0;
    const limitUsage = feature.limit_usage;
    const isUnlimited = limitUsage === null || limitUsage === undefined || limitUsage < 0;

    return {
      featureCode,
      featureName: feature.name,
      usageCount,
      limitUsage,
      limitPeriod: feature.limit_period,
      remaining: isUnlimited ? null : Math.max(0, limitUsage - usageCount),
      isUnlimited,
      isExhausted: !isUnlimited && usageCount >= limitUsage,
    };
  }, [getFeature]);

  return {
    // State
    subscription,
    features,
    uiElements,
    loading,
    error,
    initialized,
    isLoaded,
    hasSubscription,

    // Actions
    fetchUserFeatures,
    canAccessElement,
    canAccessFeature,
    getFeatureLimit,
    getElementFeature,
    hasFeature,
    getFeature,

    // Usage checking
    canUseElement,
    isUsageExhausted,
    getElementUsageInfo,

    // Legacy actions (kept for compatibility)
    hasRemainingUsage: (featureCode) => {
      const feature = getFeature(featureCode);
      if (!feature) return false;
      
      // If no limit or limit is -1 or null, it's unlimited
      if (feature.limit_usage === null || feature.limit_usage === undefined || feature.limit_usage < 0) {
        return true;
      }

      // Check remaining usage
      return (feature.usage_count || 0) < feature.limit_usage;
    },

    canUseFeature: (featureCode) => {
      // 1. Check permission
      if (!canAccessFeature(featureCode)) return false;

      // 2. Check usage limit
      const feature = getFeature(featureCode);
      if (!feature) return true; // Default to true if feature not found but access allowed (e.g. public)

      // If no limit, allow
      if (feature.limit_usage === null || feature.limit_usage === undefined || feature.limit_usage < 0) {
        return true;
      }

      // Check usage
      return (feature.usage_count || 0) < feature.limit_usage;
    },
  };
};

export default useFeatureAccess;

